import React from "react";
import {
  createBrowserRouter,
  matchRoutes as reactRouterMatchRoutes,
} from "react-router";
import Homepage, { loader as homepageLoader } from "./page/Homepage";
import Calendar, { loader as calendarLoader } from "./page/Calendar";
import Products, { loader as companyLoader } from "./page/Products.jsx";
import Dashboard, { loader as dashboardLoader } from "./page/Dashboard.jsx";
import Employees, { loader as employeesLoader } from "./page/Employees.jsx";
import Client, { loader as clientLoader } from "./page/Client";
import Clients, { loader as clientsLoader } from "./page/Clients";
import History, { loader as historyLoader } from "./page/History";
import ServiceEdit, {
  loader as serviceEditLoader,
} from "./page/ServiceEditPage";
import Reception, { loader as receptionLoader } from "./page/Reception";
import SeatsLive, { loader as seatsLiveLoader } from "./page/SeatsLive";
import Locations, { loader as locationsLoader } from "./page/Locations";
import Product, { loader as productLoader } from "./page/Product";
import RFIDAuth, { loader as rfidAuthLoader } from "./page/RFIDAuth";
import Algorithm, {
  loader as reservationSettingsLoader,
} from "./page/Algorithm.jsx";
import AlgorithmEdit, {
  loader as reservationSettingsEditLoader,
} from "./page/AlgorithmEdit.jsx";
import LocationEdit, {
  loader as locationEditLoader,
} from "./page/LocationEdit";
import Resources, { loader as calendarsLoader } from "./page/Resources.jsx";
import ResourceEdit, {
  loader as resourceEditLoader,
} from "./page/ResourceEdit.jsx";
import ErrorElement from "./components/ErrorElement";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./page/Login";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import * as auth from "./auth";
import { currentLocationFromRequest } from "./components/LocationSelector";
import ClientAppointment, {
  loader as clientAppointmentLoader,
} from "./page/ClientAppointment";
import LoginLayout from "./components/LoginLayout.jsx";
import WebhookConfig, {
  loader as webhookConfigLoader,
} from "./page/WebhookConfig.jsx";
import Cleaning from "./page/Cleaning";
import Settings, { loader as settingsLoader } from "./page/Settings";

const error404 = {
  data: "",
  internal: false,
  status: 404,
  statusText: "Not Found",
};

async function dataStrategy({ matches, request }) {
  const matchesToLoad = matches.filter((m) => m.shouldLoad);

  const authToken = auth.tokenPayload(auth.getAuthenticationToken());
  const currentLocation = currentLocationFromRequest(request);
  const currentRoles = new Set(authToken?.roles?.[currentLocation] ?? []);
  if (
    !matchesToLoad.every((match) =>
      isGrantedAny(currentRoles, match.route?.handle?.anyRole),
    )
  ) {
    throw error404;
  }

  const results = await Promise.all(matchesToLoad.map((m) => m.resolve()));
  return results.reduce(
    (acc, result, i) =>
      Object.assign(acc, { [matchesToLoad[i].route.id]: result }),
    {},
  );
}

/** @type {import('react-router').RouteObject} */
export const routes = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Homepage />
      </ProtectedRoute>
    ),
    loader: homepageLoader,
    errorElement: <ErrorElement />,
  },
  {
    path: "/calendar",
    element: (
      <ProtectedRoute>
        <Calendar />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: calendarLoader,
    handle: {
      anyRole: new Set([
        role.Back_Office,
        role.Front_Office,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/products",
    element: (
      <ProtectedRoute>
        <Products />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: companyLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: dashboardLoader,
    handle: {
      anyRole: new Set([role.Floor_Manager, role.Manager_Clinic]),
    },
  },
  {
    path: "/employees",
    element: (
      <ProtectedRoute>
        <Employees />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: employeesLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Manager_Clinic,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/history",
    element: (
      <ProtectedRoute>
        <History />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: historyLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Customer_Care,
        role.Manager_Clinic,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute>
        <Settings />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: settingsLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
      ]),
    },
  },
  {
    path: "/resources",
    element: (
      <ProtectedRoute>
        <Resources />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: calendarsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/resources/:id/edit",
    element: (
      <ProtectedRoute>
        <ResourceEdit />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: resourceEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/clients",
    element: (
      <ProtectedRoute>
        <Clients />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: clientsLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/clients/:id",
    element: (
      <ProtectedRoute>
        <Client />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: clientLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/clients/:id/:appointmentId",
    element: (
      <ProtectedRoute>
        <ClientAppointment />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: clientAppointmentLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
        role.Dental_Assistant, //to allow nurse to see client appointments
      ]),
    },
  },
  {
    path: "/products/:id",
    element: (
      <ProtectedRoute>
        <Product />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: productLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/reception",
    element: (
      <ProtectedRoute>
        <Reception />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: receptionLoader,
    handle: {
      anyRole: new Set([role.Front_Office, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/seats-live",
    element: (
      <ProtectedRoute>
        <SeatsLive />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: seatsLiveLoader,
    handle: {
      anyRole: new Set([
        role.Front_Office,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/algorithm",
    element: (
      <ProtectedRoute>
        <Algorithm />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: reservationSettingsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/algorithm/edit",
    element: (
      <ProtectedRoute>
        <AlgorithmEdit />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: reservationSettingsEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/products/services/:id/edit",
    element: (
      <ProtectedRoute>
        <ServiceEdit />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: serviceEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/locations",
    element: (
      <ProtectedRoute>
        <Locations />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: locationsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Floor_Manager,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/locations/:id/edit",
    element: (
      <ProtectedRoute>
        <LocationEdit />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: locationEditLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Floor_Manager,
      ]),
    },
  },
  {
    path: "/webhooks",
    element: (
      <ProtectedRoute>
        <WebhookConfig />
      </ProtectedRoute>
    ),
    errorElement: <ErrorElement />,
    loader: webhookConfigLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/login",
    element: (
      <LoginLayout>
        <LoginPage />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
  },
  {
    path: "/rfid-auth",
    element: (
      <LoginLayout>
        <RFIDAuth />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
    loader: rfidAuthLoader,
  },
  {
    path: "/cleaning",
    element: (
      <LoginLayout>
        <Cleaning />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
  },
  {
    path: "*",
    errorElement: <ErrorElement />,
    loader: function () {
      throw new Response("Not Found", { status: 404, statusText: "Not Found" });
    },
  },
];

export function matchRoutes(path) {
  return reactRouterMatchRoutes(routes, path);
}

export const anyRoleByPath = routes.reduce((acc, route) => {
  const anyRole = route.handle?.anyRole;
  if (anyRole != null) {
    acc[route.path] = anyRole;
  }

  return acc;
}, {});

export const router = createBrowserRouter(routes, {
  dataStrategy: dataStrategy,
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
